import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import BrandLogo from '../BrandLogo';
import { useAuth } from '../../context/AuthContext';
import billingService from '../../services/billingService';

function Subscribe() {
  const { isAuthenticated, isLoading: authLoading, checkAuthStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutResult = searchParams.get('checkout');
  const [billing, setBilling] = useState(null);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;
    let poll;
    const load = async () => {
      try {
        const data = await billingService.status();
        if (!cancelled) {
          setBilling(data);
          if (data.plan_tier === 'pro') {
            if (poll) window.clearInterval(poll);
            await checkAuthStatus();
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };
    load();
    if (checkoutResult === 'success') {
      poll = window.setInterval(load, 2500);
    }
    return () => {
      cancelled = true;
      if (poll) window.clearInterval(poll);
    };
    // checkAuthStatus intentionally runs only after a fresh billing response;
    // it is not a stable callback in the legacy auth context.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, checkoutResult]);

  if (authLoading) return <div className="purchase-shell"><p>Loading…</p></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const beginCheckout = async () => {
    setWorking(true);
    setError('');
    try {
      const { url } = await billingService.checkout();
      window.location.assign(url);
    } catch (err) {
      setError(err.message);
      setWorking(false);
    }
  };

  const manageBilling = async () => {
    setWorking(true);
    setError('');
    try {
      const { url } = await billingService.portal();
      window.location.assign(url);
    } catch (err) {
      setError(err.message);
      setWorking(false);
    }
  };

  const isPro = billing?.plan_tier === 'pro';

  return (
    <main className="purchase-shell">
      <section className="purchase-card" aria-labelledby="purchase-title">
        <BrandLogo />
        <p className="purchase-eyebrow">Cloudnotes plans</p>
        <h1 id="purchase-title">Start free. Upgrade when it fits.</h1>
        <p className="purchase-lede">
          Every Cloudnotes account includes free access to your notes. Pro is an optional upgrade
          for additional features as they become available.
        </p>

        <div className="purchase-current" role="status" aria-atomic="true">
          Current plan: <strong>{isPro ? 'Pro' : 'Free'}</strong>
        </div>

        <div className="purchase-plan">
          <div>
            <strong>{billing?.plan?.label || 'Cloudnotes Pro'}</strong>
            <span>Secure notes, AI tools, and transcription</span>
          </div>
          <strong>{billing?.plan?.price || 'Loading…'}</strong>
        </div>

        {checkoutResult === 'success' && (
          <p className="purchase-status" role="status" aria-atomic="true">
            {isPro ? 'Upgrade confirmed. Cloudnotes Pro is active.' : 'Payment received. Confirming your upgrade…'}
          </p>
        )}
        {checkoutResult === 'cancelled' && (
          <p className="purchase-status">Checkout was cancelled. Your account is unchanged.</p>
        )}
        {error && <p className="purchase-error" role="alert">{error}</p>}

        <button
          className="purchase-primary"
          type="button"
          onClick={isPro ? manageBilling : beginCheckout}
          disabled={working || (!isPro && !billing?.billing_configured)}
        >
          {working ? 'Opening secure billing…' : isPro ? 'Manage Pro billing' : 'Upgrade to Pro'}
        </button>
        {!billing?.billing_configured && billing && (
          <p className="purchase-help">Pro upgrades are coming soon. Your Free plan is ready now.</p>
        )}
        <button className="purchase-secondary" type="button" onClick={() => navigate('/notes')}>
          Continue to notes
        </button>
        <p className="purchase-product">A product of Inframodule</p>
      </section>
    </main>
  );
}

export default Subscribe;
